import { useState, useEffect } from 'react';
import { api } from '../services/api';
import {
  getProducts as getCachedProducts,
  saveProducts,
  getManufacturers as getCachedManufacturers,
  saveManufacturers,
  getPoSList as getCachedPoSList,
  savePoSList,
  getDistributors as getCachedDistributors,
  saveDistributors,
} from '../services/offlineStorage';
import { useOnlineStatus } from './useOnlineStatus';

/**
 * Hook to get products with offline-first strategy
 * 1. Load from cache immediately
 * 2. Fetch fresh data in background if online
 * 3. Update cache and state with fresh data
 */
export function useProducts() {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOnline } = useOnlineStatus();

  useEffect(() => {
    let mounted = true;

    async function loadProducts() {
      try {
        // 1. Try to load from cache first
        const cached = await getCachedProducts();
        if (cached && cached.length > 0) {
          if (mounted) {
            setProducts(cached);
            setLoading(false);
          }
        }

        // 2. If online, fetch fresh data
        if (isOnline) {
          try {
            const res = await api.get('/products?is_active=true');
            if (res.data && mounted) {
              setProducts(res.data);
              // Update cache in background
              await saveProducts(res.data);
            }
          } catch (apiError: any) {
            console.warn('[useProducts] API fetch failed, using cached data', apiError);
            // If we have cached data, don't set error
            if (!cached || cached.length === 0) {
              setError('Failed to load products');
            }
          }
        }

        if (mounted) {
          setLoading(false);
        }
      } catch (err: any) {
        console.error('[useProducts] Error:', err);
        if (mounted) {
          setError(err.message || 'Failed to load products');
          setLoading(false);
        }
      }
    }

    loadProducts();

    return () => {
      mounted = false;
    };
  }, [isOnline]);

  return { products, loading, error };
}

/**
 * Hook to get manufacturers with offline-first strategy
 */
export function useManufacturers() {
  const [manufacturers, setManufacturers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOnline } = useOnlineStatus();

  useEffect(() => {
    let mounted = true;

    async function loadManufacturers() {
      try {
        const cached = await getCachedManufacturers();
        if (cached && cached.length > 0) {
          if (mounted) {
            setManufacturers(cached);
            setLoading(false);
          }
        }

        if (isOnline) {
          try {
            const res = await api.get('/manufacturers');
            if (res.data && mounted) {
              setManufacturers(res.data);
              await saveManufacturers(res.data);
            }
          } catch (apiError: any) {
            console.warn('[useManufacturers] API fetch failed, using cached data', apiError);
            if (!cached || cached.length === 0) {
              setError('Failed to load manufacturers');
            }
          }
        }

        if (mounted) setLoading(false);
      } catch (err: any) {
        console.error('[useManufacturers] Error:', err);
        if (mounted) {
          setError(err.message || 'Failed to load manufacturers');
          setLoading(false);
        }
      }
    }

    loadManufacturers();
    return () => { mounted = false; };
  }, [isOnline]);

  return { manufacturers, loading, error };
}

// Note: usePoSList removed - PoS search requires internet connection
// PoS selection should be done before entering stores when online

/**
 * Hook to get distributors list with offline-first strategy
 */
export function useDistributors() {
  const [distributors, setDistributors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOnline } = useOnlineStatus();

  useEffect(() => {
    let mounted = true;

    async function loadDistributors() {
      try {
        // 1. Try to load from cache first
        const cached = await getCachedDistributors();
        if (cached && cached.length > 0) {
          if (mounted) {
            setDistributors(cached);
            setLoading(false);
          }
        }

        // 2. If online, fetch fresh data
        if (isOnline) {
          try {
            const res = await api.get('/distributors');
            if (res.data && mounted) {
              setDistributors(res.data);
              // Update cache in background
              await saveDistributors(res.data);
            }
          } catch (apiError: any) {
            console.warn('[useDistributors] API fetch failed, using cached data', apiError);
            // If we have cached data, don't set error
            if (!cached || cached.length === 0) {
              setError('Failed to load distributors');
            }
          }
        }

        if (mounted) {
          setLoading(false);
        }
      } catch (err: any) {
        console.error('[useDistributors] Error:', err);
        if (mounted) {
          setError(err.message || 'Failed to load distributors');
          setLoading(false);
        }
      }
    }

    loadDistributors();

    return () => {
      mounted = false;
    };
  }, [isOnline]);

  return { distributors, loading, error };
}
